import { useCallback, useEffect, useRef } from "react";
import type RTKClient from "@cloudflare/realtimekit";

function getHostMediaRecordingMimeType(): string {
  const candidates = [
    "video/mp4;codecs=avc1.640028,mp4a.40.2",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function downloadRecordingBlob({
  blob,
  mimeType,
}: {
  blob: Blob;
  mimeType: string;
}) {
  const extension = mimeType.includes("mp4") ? "mp4" : "webm";
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `host-recording-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildRecordingStream({
  videoTrack,
  audioTrack,
}: {
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
}): MediaStream | null {
  const tracks: MediaStreamTrack[] = [];
  if (videoTrack) tracks.push(videoTrack);
  if (audioTrack) tracks.push(audioTrack);
  if (tracks.length === 0) return null;
  return new MediaStream(tracks);
}

export function useHostMediaLocalRecording({
  meeting,
  videoTrack,
  audioTrack,
}: {
  meeting: RTKClient | undefined;
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
}) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startHostRecording = useCallback(async () => {
    if (!meeting || recorderRef.current) return;

    const mimeType = getHostMediaRecordingMimeType();
    if (!mimeType) return;

    const stream = buildRecordingStream({ videoTrack, audioTrack });
    if (!stream) return;

    chunksRef.current = [];

    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.start(1000);
    recorderRef.current = recorder;
  }, [meeting, videoTrack, audioTrack]);

  const stopHostRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    recorderRef.current = null;

    recorder.onstop = () => {
      const mimeType = recorder.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      if (blob.size > 0) {
        downloadRecordingBlob({ blob, mimeType });
      }
    };
    recorder.stop();
  }, []);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      recorderRef.current = null;
      chunksRef.current = [];
    };
  }, []);

  return { startHostRecording, stopHostRecording };
}
